import { Trans, useLingui } from "@lingui/react/macro";
import type {
  AgentSkillCatalogEntry,
  Bot,
  ComputerStatus,
  Group,
  ThreadSnapshot,
} from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import { X } from "lucide-react";
import type { ComponentProps, Dispatch, ReactNode, SetStateAction } from "react";
import { ComputerMaintenanceActions } from "../../components/ComputerMaintenanceActions";
import { desktopBridge, windowChromeKind } from "../../lib/desktop";
import { type Panel, panelForChat } from "../../lib/right-panel-state";
import type { createSpaceRpc } from "../../lib/rpc";
import { computerPanelNeedsMaintenance } from "../../lib/thread-events";
import { GroupSettings } from "../GroupPanel";
import { ResizableSidePanel } from "../ResizableSidePanel";
import { RoutineEditor } from "../RoutineEditor";
import type { SettingsSection } from "../shell-overlay";
import { WindowChrome } from "../WindowChrome";
import type { BotPanelTab } from "./bot-panel";
import { BotSettings } from "./bot-panel";
import { CompanyComputerPanel, CompanyComputerTabs, useCompanyComputers } from "./company-computer";

/** The panel beside the chat: a bot's tabs, a routine, a group, or an import. */
export function ShellSidePanel({
  rpc,
  panel,
  setPanel,
  active,
  activeGroup,
  company,
  bots,
  setBots,
  setGroups,
  removeGroup,
  refreshBots,
  refreshThread,
  refreshGroupThread,
  computer,
  computerOpen,
  booting,
  computerPanel,
  routinesPanel,
  routineEditorProps,
  openBot,
  modelFocusRequest,
  openSettings,
  memoryProviderConfigured,
  setAgentSkills,
  onClearBot,
  permanent,
}: {
  rpc: ReturnType<typeof createSpaceRpc>;
  panel: Panel;
  setPanel: (panel: Panel) => void;
  active: Bot | undefined;
  activeGroup: Group | undefined;
  company: boolean;
  bots: Bot[];
  setBots: Dispatch<SetStateAction<Bot[]>>;
  setGroups: Dispatch<SetStateAction<Group[]>>;
  removeGroup: (group: Group) => Promise<void>;
  refreshBots: (includeArchived?: boolean) => Promise<void>;
  refreshThread: (botId: string) => Promise<ThreadSnapshot>;
  refreshGroupThread: (groupId: string) => Promise<ThreadSnapshot>;
  computer: ComputerStatus | null;
  computerOpen: boolean;
  booting: boolean;
  computerPanel: ReactNode;
  routinesPanel: ReactNode;
  routineEditorProps: ComponentProps<typeof RoutineEditor> | null;
  openBot: (botId: string) => void;
  modelFocusRequest: number;
  openSettings: (section: SettingsSection) => void;
  memoryProviderConfigured: boolean;
  setAgentSkills: (skills: AgentSkillCatalogEntry[]) => void;
  onClearBot: (bot: Bot) => void;
  /** Desktop keeps the panel open; phones still open it on demand. */
  permanent: boolean;
}) {
  const { t } = useLingui();
  const shownPanel = panelForChat(panel, {
    group: Boolean(activeGroup),
    bot: Boolean(active),
    routineReady: Boolean(routineEditorProps),
  });
  const open = Boolean((active || activeGroup) && (permanent || panel));
  const permanentHeader = open && permanent;
  const companyGroup = activeGroup?.isCompany ?? company;
  const showCompanyComputers = Boolean(
    companyGroup && activeGroup && shownPanel === "group-settings",
  );
  const computers = useCompanyComputers({
    rpc,
    bots,
    group: activeGroup,
    enabled: showCompanyComputers,
  });
  const botTab =
    shownPanel === "computer" || shownPanel === "routines" || shownPanel === "settings"
      ? shownPanel
      : null;
  const panelTitle = activeGroup
    ? companyGroup
      ? t`Company overview`
      : t`Group settings`
    : shownPanel === "computer"
      ? t`Computer`
      : shownPanel === "routines"
        ? t`Routines`
        : shownPanel === "routine"
          ? t`Routine`
          : (active?.name ?? t`Settings`);
  const desktop = desktopBridge();
  const showWindowControls = windowChromeKind(desktop) === "controls";
  const showMaintenance =
    Boolean(active && shownPanel === "computer" && !computerOpen) &&
    computerPanelNeedsMaintenance(computer?.state, booting);

  return (
    <ResizableSidePanel open={open} panel={shownPanel ?? "closed"}>
      {open ? (
        <>
          {permanentHeader ? (
            <div className="app-drag flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border/50 px-2">
              {showCompanyComputers && computers.entries.length ? (
                <CompanyComputerTabs
                  entries={computers.entries}
                  statuses={computers.statuses}
                  selectedKey={computers.selectedKey}
                  onSelect={computers.setSelectedKey}
                />
              ) : (
                <span className="min-w-0 truncate px-1.5 text-ui-base font-medium text-foreground">
                  {panelTitle}
                </span>
              )}
              <div className="app-no-drag flex shrink-0 items-center gap-1">
                {showMaintenance && active ? (
                  <ComputerMaintenanceActions
                    botId={active.id}
                    computer={computer}
                    onChanged={async () => {
                      await refreshThread(active.id);
                    }}
                  />
                ) : null}
                {showWindowControls ? <WindowChrome /> : null}
              </div>
            </div>
          ) : null}
          <div
            className={`rk-scroll min-h-0 flex-1 overflow-y-auto px-5 ${permanentHeader ? "py-4" : "py-[17px]"}`}
          >
            {!permanentHeader && shownPanel !== "routine" && shownPanel !== "group-settings" ? (
              <div className="mb-4 flex items-center justify-between">
                {!active && shownPanel !== "settings" ? (
                  <span className="text-[13.5px] text-muted-foreground">
                    <Trans>Group</Trans>
                  </span>
                ) : null}
                <div className="ml-auto flex gap-1">
                  {showMaintenance && active ? (
                    <ComputerMaintenanceActions
                      botId={active.id}
                      computer={computer}
                      onChanged={async () => {
                        await refreshThread(active.id);
                      }}
                    />
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t`Close panel`}
                    onClick={() => setPanel(null)}
                  >
                    <X size={16} strokeWidth={1.8} />
                  </Button>
                </div>
              </div>
            ) : null}
            {shownPanel === "group-settings" && activeGroup ? (
              <GroupSettings
                key={activeGroup.id}
                group={activeGroup}
                bots={bots}
                company={companyGroup}
                rpc={rpc}
                onCompanyChanged={async () => {
                  await Promise.all([refreshBots(), refreshGroupThread(activeGroup.id)]);
                }}
                topContent={
                  companyGroup ? (
                    <CompanyComputerPanel
                      rpc={rpc}
                      entries={computers.entries}
                      selectedEntry={computers.selectedEntry}
                      selectedStatus={computers.selectedStatus}
                      onStatusChange={computers.setStatus}
                    />
                  ) : undefined
                }
                onSave={async (input) => {
                  const updated = await rpc.groups.update({ groupId: activeGroup.id, ...input });
                  setGroups((current) =>
                    current.map((group) => (group.id === updated.id ? updated : group)),
                  );
                  void Promise.all([refreshBots(), refreshGroupThread(activeGroup.id)]).catch(
                    () => undefined,
                  );
                }}
                onRemove={() => removeGroup(activeGroup)}
                onClose={() => setPanel(null)}
                onOpenBot={openBot}
                covered={computerOpen}
                showHeader={!permanentHeader}
              />
            ) : null}
            {botTab && active ? (
              <BotSettings
                key={active.id}
                bot={active}
                tab={botTab}
                onTabChange={(tab: BotPanelTab) => {
                  setPanel(tab);
                  if (tab === "computer") void refreshThread(active.id).catch(() => undefined);
                }}
                computer={computerPanel}
                routines={routinesPanel}
                focusModel={modelFocusRequest}
                onOpenConnections={() => openSettings("models")}
                memoryProviderConfigured={memoryProviderConfigured}
                onSkillsChange={setAgentSkills}
                onSave={async ({ computerMode, ...patch }) => {
                  if (computerMode !== undefined && computerMode !== active.computerMode) {
                    await rpc.bots.setComputer({
                      botId: active.id,
                      mode: computerMode,
                    });
                  }
                  const updated = await rpc.bots.update({ botId: active.id, ...patch });
                  setBots((current) =>
                    current.map((bot) => (bot.id === updated.id ? updated : bot)),
                  );
                  // Persistence succeeded; a failed navigation refresh must not undo the tool edit.
                  await refreshBots().catch(() => undefined);
                }}
                onExport={async () => {
                  const manifest = await rpc.export.bot({ botId: active.id });
                  const blob = new Blob([JSON.stringify(manifest, null, 2)], {
                    type: "application/json",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${active.name.toLowerCase().replace(/\s+/g, "-")}-export.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                onClear={() => onClearBot(active)}
              />
            ) : null}
            {shownPanel === "routine" && routineEditorProps ? (
              <RoutineEditor {...routineEditorProps} />
            ) : null}
          </div>
        </>
      ) : null}
    </ResizableSidePanel>
  );
}
